'use client';

/**
 * market-shared — small shared idioms for the chain market page (market-v2 spec,
 * docs/design/market-prototype/market-v2.html).
 *
 * Owns the zone band-rule and the text helpers the v2 zones share (scannable
 * highlighting, why-line extraction, price formatting). Extracted from the
 * retired round-1 components (MarketDeskBrief/MarketDeskReads) so the v2 zones
 * don't import dead code.
 */

import React from 'react';

// ── shared band-rule (dossier zone-rule idiom: gold mono label + hairline) ─────────
export function MarketBandRule({ label, right }: { label: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-600 dark:text-scout-gold">
      <span>{label}</span>
      <span aria-hidden className="h-px flex-1 bg-gray-200 dark:bg-divider" />
      {right && <span className="font-normal text-gray-500 dark:text-parchment-dim">{right}</span>}
    </div>
  );
}

// ── scannable summary text (token symbols / $ / %) in dossier colors ───────────────
export function scannable(text: string): React.ReactNode {
  // Tokenize ALL-CAPS symbols, dollar amounts and signed percents in one pass so
  // matches never overlap (same approach as the AssetDetailPanel formatter).
  const re = /(\$[\d,]+\.?\d*[KMB]?)|([+-]?\d+\.?\d*%)|(\b[A-Z]{2,}\b)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (m[1]) {
      parts.push(<span key={i++} className="font-semibold text-gray-900 dark:text-parchment">{tok}</span>);
    } else if (m[2]) {
      const neg = tok.startsWith('-');
      parts.push(
        <span key={i++} className={`font-semibold ${neg ? 'text-alert-red dark:text-alert-red-text' : 'text-signal-green dark:text-signal-green-text'}`}>
          {tok}
        </span>,
      );
    } else {
      parts.push(<span key={i++} className="font-semibold text-signal-green dark:text-signal-green-text">{tok}</span>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : text;
}

// ── why-line extraction ─────────────────────────────────────────────────────────────
// The mover tiles carry ONE line of narrative from the asset's produced recon read.
// The desk's insight docs open with a numbers-boilerplate sentence ("X trades at $… on
// Abstract, up N% …") that would just restate the tile's own big number — when the
// opener matches that boilerplate and a later sentence exists, the LAST sentence (the
// narrative driver in the docs' consistent structure) is used instead. The text is
// always a verbatim clip of the produced read — never synthesized.
/** Decimal-safe sentence split: a [.!?] ends a sentence only before whitespace/end
 *  (so "$7.63" / "+0.6%" never fragment). No lookbehind (older-Safari-safe). */
function splitSentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if ((c === '.' || c === '!' || c === '?') && (i + 1 >= text.length || text[i + 1] === ' ')) {
      const s = text.slice(start, i + 1).trim();
      if (s) out.push(s);
      start = i + 1;
    }
  }
  const rest = text.slice(start).trim();
  if (rest) out.push(rest);
  return out;
}

export function whyLine(analysis: string, maxChars = 200): string {
  const plain = analysis.replace(/\s+/g, ' ').trim();
  const sentences = splitSentences(plain);
  let line = sentences[0] ?? plain;
  if (sentences.length > 1 && /^[\w$ ]{1,40}\btrades at \$/i.test(line)) {
    line = sentences[sentences.length - 1];
  }
  if (line.length > maxChars) {
    const cut = line.slice(0, maxChars);
    const sp = cut.lastIndexOf(' ');
    line = (sp > maxChars * 0.6 ? cut.slice(0, sp) : cut).trimEnd() + '…';
  }
  return line;
}

// ── token price formatting (same tiering as the tape's formatter) ───────────────────
export function fmtTokenPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  if (price >= 1) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}
